/**
 * Jump to a legajo by name or CUIT, from anywhere, with ⌘K.
 *
 * A credit team works a queue of hundreds. Scrolling a sidebar to find "Molinos del Sur" is the
 * kind of small friction that, repeated forty times a day, is what people mean when they say a
 * tool is slow.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import { useFocusReturn } from "../lib/focus";
import { caseRisk, caseState, searchCases } from "../lib/model";
import { Chip, Dot } from "./ui";

const MAX = 8;

export default function CommandPalette({
  onPick,
  onClose,
}: {
  onPick: (caseId: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const dialog = useRef<HTMLDivElement>(null);

  // La búsqueda pasa en el servidor: con una cartera de miles, filtrar en el navegador obliga a
  // traerla entera para escribir tres letras. El filtro local sigue aplicándose sobre la página
  // que vuelve, que es barato y deja la lista ordenada igual que la cartera.
  const cases = useQuery({
    queryKey: ["cases", "palette", query],
    queryFn: () => api.listCases({ q: query, size: MAX }),
    placeholderData: (previous) => previous,
  });
  const results = useMemo(
    () => searchCases(cases.data?.items ?? [], query).slice(0, MAX),
    [cases.data, query],
  );

  // El foco entra al buscador al abrirse y vuelve al control que lo abrió al cerrarse: quien
  // navega con teclado no tiene que recorrer la pantalla otra vez para volver a donde estaba.
  useFocusReturn(dialog);
  useEffect(() => setCursor(0), [query]);

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Escape") return onClose();
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setCursor((c) => Math.min(c + 1, results.length - 1));
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    }
    if (event.key === "Enter" && results[cursor]) {
      event.preventDefault();
      onPick(results[cursor]!.id);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-ink/40 px-6 pt-[12vh]"
      onClick={onClose}
    >
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-label="Buscar legajo"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-2xl"
      >
        <input
          value={query}
          role="combobox"
          aria-expanded={results.length > 0}
          aria-controls="palette-results"
          aria-activedescendant={results[cursor] ? `palette-${results[cursor]!.id}` : undefined}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Buscar por empresa o CUIT…"
          className="w-full border-b border-line px-4 py-3.5 text-sm focus:outline-none"
        />

        {results.length === 0 ?
          <p className="px-4 py-6 text-center text-sm text-muted">
            {cases.isLoading ?
              "Cargando la cartera…"
            : query ?
              "Ningún legajo coincide."
            : "Escribí para buscar."}
          </p>
        : <ul id="palette-results" role="listbox">
            {results.map((kase, index) => {
              const state = caseState(kase);
              const risk = caseRisk(kase);
              return (
                <li
                  key={kase.id}
                  id={`palette-${kase.id}`}
                  role="option"
                  aria-selected={index === cursor}
                >
                  <button
                    tabIndex={-1}
                    onMouseEnter={() => setCursor(index)}
                    onClick={() => onPick(kase.id)}
                    className={`flex w-full items-center justify-between gap-4 px-4 py-2.5 text-left ${
                      index === cursor ? "bg-canvas" : ""
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{kase.legal_name}</span>
                      <span className="mt-0.5 flex items-center gap-2 text-xs text-muted">
                        <Dot tone={state.tone} />
                        {state.text} · {kase.cuit}
                      </span>
                    </span>
                    {risk ?
                      <Chip text={risk.text} tone={risk.tone} />
                    : null}
                  </button>
                </li>
              );
            })}
          </ul>
        }

        <div className="flex gap-4 border-t border-line px-4 py-2 text-[0.7rem] text-muted">
          <span>↑↓ moverse</span>
          <span>↵ abrir</span>
          <span>esc cerrar</span>
        </div>
      </div>
    </div>
  );
}
